import React, { useState, useEffect, useRef } from "react";
import MetaTags from "components/MetaTags";
import { Container, ContainerWithOffset } from "components/Elements/Container";
import AnimatedBox from "components/Elements/AnimatedBox";
import { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/css";
import parse from "html-react-parser";
import API from "strapi/api";
import useMouse from "@react-hook/mouse-position";
import ImageGliph from "components/Elements/ImageGlliph";

const TeamMemberCard = ({ data }) => {
  const { photo, name, position } = data;

  const card = useRef(null);
  const { x, y } = useMouse(card, {
    leaveDelay: 300,
  });

  return (
    <div className="relative !bg-transparent image-container" ref={card}>
      {data?.line && (
        <div
          style={{ left: x && x + 25, top: y && y }}
          className="absolute z-50 px-6 py-3 uppercase transition-opacity duration-500 bg-white border-2 border-black opacity-0 team-member-slide-tooltip text-16"
        >
          {data?.line}
        </div>
      )}
      <Image
        src={API.getNextImage(photo, "large")}
        objectFit="cover"
        layout="fill"
        loading="eager"
        alt=""
      />
      <p className="mt-2 leading-6 text-16 768:text-18 1280:text-22">
        <span className="uppercase animated-border dark-border thin-border">
          {name}
          <span className="absolute top-0 left-0 w-full h-full"></span>
        </span>
        <br />
        <span>{position}</span>
      </p>
    </div>
  );
};

const Modal = ({ data, isModalOpen, closeModal }) => {
  return (
    <>
      <div
        onClick={closeModal}
        className={`close-overlay fixed w-screen h-screen z-50 top-0 left-0 bg-white/60 transition-opacity duration-500 
                ${
                  isModalOpen
                    ? "opacity-100 pointer-events-auto"
                    : "opacity-0 pointer-events-none"
                }`}
      ></div>
      <aside
        id="members-modal"
        className={`fixed h-screen top-0 bg-white z-50 py-4 px-5 transition-all duration-500 overflow-y-auto 768:border-r-2 border-black ${
          isModalOpen ? "left-0" : "-left-full"
        }`}
      >
        <div className="flex items-start justify-between w-full">
          <div className="">
            <h3 className="mb-2 leading-none uppercase fluid-type">
              {data?.name}
            </h3>
            <h4 className="leading-none ">{data?.position}</h4>
          </div>
          <button
            onClick={closeModal}
            className="leading-6 uppercase text-16 768:text-18 1280:text-22 animated-border dark-border thin-border"
          >
            close
          </button>
        </div>
        <div
          id="member-description"
          className="pb-6 mt-5 leading-snug 768:mt-16 991:mt-24 indent-10 768:indent-20 1024:indent-24 content-with-glyphs"
        >
          {data && parse(data.bio)}
        </div>
      </aside>
    </>
  );
};

export default function About(props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const handleKeyPress = (event) => {
    if (event.keyCode === 27) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress, { passive: true });
  }, []);

  const navigationSlideshowStudioPrev = useRef(null);
  const navigationSlideshowStudioNext = useRef(null);
  const navigationSlideshowTeamPrev = useRef(null);
  const navigationSlideshowTeamNext = useRef(null);

  return (
    <>
      <MetaTags
        title={
          props.meta_info && props.meta_info.meta_title
            ? props.meta_info.meta_title
            : "ABOUT US"
        }
        description={
          props.meta_info && props.meta_info.meta_description
            ? props.meta_info.meta_description
            : "ABOUT US - ZANAD | An Independent Animation Production Studio"
        }
        image={
          props.meta_info && props.meta_info.og_image
            ? API.getNextImage(props.meta_info.og_image, "large")
            : "/public/og-image.jpg"
        }
      />
      <main>
        <h1 className="offscreen-title">
          {props.meta_info && props.meta_info.meta_title
            ? props.meta_info.meta_title
            : "ABOUT US"}
        </h1>
        <Modal
          data={modalData}
          isModalOpen={isModalOpen}
          closeModal={closeModal}
        />
        <ContainerWithOffset>
          <p className="mt-8 mb-6 uppercase fluid-type">
            <AnimatedBox num={1}>
              <span className="flex flex-row justify-start">
                <span className="optical-align letter-d">
                  <AnimatedBox num={5}>Down </AnimatedBox>
                  <AnimatedBox num={1.5}>with</AnimatedBox>
                  <AnimatedBox num={3.5}>
                    <ImageGliph src="MARGIN_HAND" />
                  </AnimatedBox>
                  <AnimatedBox num={6}>anything</AnimatedBox>
                </span>
              </span>
            </AnimatedBox>
            <AnimatedBox num={2}>
              <span className="flex flex-row justify-start">
                <span>
                  <AnimatedBox num={2}>that </AnimatedBox>
                  <AnimatedBox num={4.5}>moves </AnimatedBox>
                  <AnimatedBox num={7}>ideas</AnimatedBox>
                </span>
              </span>
            </AnimatedBox>
            <AnimatedBox num={3}>
              <span className="flex flex-row justify-end">
                <span className="inline-block optical-align letter-p">
                  <AnimatedBox num={3.5}>people </AnimatedBox>
                  <AnimatedBox num={1}>and </AnimatedBox>
                  <AnimatedBox num={6.5}>brands</AnimatedBox>
                </span>
              </span>
            </AnimatedBox>
            <AnimatedBox num={4}>
              <span className="flex flex-row justify-end">
                <span>
                  <AnimatedBox num={5}>
                    <ImageGliph src="MARGIN_CAT" />
                  </AnimatedBox>
                  <AnimatedBox num={1}>forward.</AnimatedBox>
                </span>
              </span>
            </AnimatedBox>
          </p>
          <div className="mb-6 leading-tight text-20 768:text-30 1024:text-40 1280:text-48 indent-10 768:indent-20 1024:indent-36 1280:indent-48 content-with-glyphs">
            {props.content.lead ? parse(props.content.lead) : <></>}
          </div>
        </ContainerWithOffset>

        {props.content.gallery ? (
          <Swiper
            modules={[Navigation]}
            spaceBetween={0}
            slideToClickedSlide={true}
            grabCursor={true}
            navigation={{
              prevEl: navigationSlideshowStudioPrev.current,
              nextEl: navigationSlideshowStudioNext.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl =
                navigationSlideshowStudioPrev.current;
              swiper.params.navigation.nextEl =
                navigationSlideshowStudioNext.current;
            }}
            slidesPerView="auto"
            className="mb-6 studio-slideshow carousel group"
          >
            {props.content.gallery.data.map((slide, i) => {
              return (
                <SwiperSlide
                  className="relative !bg-transparent image-container"
                  key={i}
                >
                  <Image
                    src={
                      process.env.NEXT_PUBLIC_SERVER_STRAPI_API +
                      slide.attributes.url
                    }
                    objectFit="cover"
                    layout="fill"
                    loading="eager"
                    alt=""
                  />
                </SwiperSlide>
              );
            })}

            <div className="absolute z-10 w-full px-5 transition-opacity -translate-y-1/2 opacity-0 pointer-events-none navigation-wrapper top-1/2 768:px-8 duration-400 group-hover:opacity-100">
              <div
                ref={navigationSlideshowStudioPrev}
                className="cursor-pointer pointer-events-auto slideshow-control prev-control"
              >
                <svg
                  width="52"
                  height="52"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="26"
                    cy="26"
                    r="25"
                    fill="white"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M22.7319 36.9628L13.186 27.4168C12.6002 26.831 12.6002 25.8813 13.186 25.2955L22.7319 15.7496L24.8532 17.8709L17.868 24.8562L38.4658 24.8562L38.4658 27.8562L17.868 27.8562L24.8532 34.8414L22.7319 36.9628Z"
                    fill="black"
                  />
                </svg>
              </div>
              <div
                ref={navigationSlideshowStudioNext}
                className="absolute top-0 cursor-pointer pointer-events-auto slideshow-control next-control right-4 768:right-8"
              >
                <svg
                  width="52"
                  height="52"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="26"
                    cy="26"
                    r="25"
                    transform="rotate(-180 26 26)"
                    fill="white"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M29.2681 15.0372L38.814 24.5832C39.3998 25.169 39.3998 26.1187 38.814 26.7045L29.2681 36.2504L27.1468 34.1291L34.132 27.1438L13.5342 27.1438L13.5342 24.1438L34.132 24.1438L27.1468 17.1586L29.2681 15.0372Z"
                    fill="black"
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        ) : (
          <></>
        )}

        <Container>
          <div className="grid gap-4 mb-24 640:gap-12 768:gap-10 1024:gap-16 640:grid-cols-2">
            <h2 className="uppercase fluid-type">
              <AnimatedBox num={1}>
                <span className="block optical-align letter-o">Our</span>
              </AnimatedBox>
              <AnimatedBox num={2}>
                <span className="block text-right 768:text-left 768:indent-20 1024:indent-40 1280:indent-56">
                  process
                </span>
              </AnimatedBox>
            </h2>
            <div className="max-w-screen-md leading-tight text-16 768:text-18 1280:text-22 content-with-glyphs">
              {props.content.process ? parse(props.content.process) : <></>}
            </div>
          </div>
          <div className="mb-6">
            <h2 className="uppercase fluid-type">
              <AnimatedBox num={1}>
                <span className="block 768:indent-20 1024:indent-40 1280:indent-56 optical-align letter-o">
                  Our
                </span>
              </AnimatedBox>
              <AnimatedBox num={2}>
                <span className="block text-right 768:text-left optical-align letter-d">
                  people
                </span>
              </AnimatedBox>
            </h2>
          </div>
        </Container>

        {props.content.team ? (
          <Swiper
            modules={[Navigation]}
            spaceBetween={0}
            preventClicks={false}
            navigation={{
              prevEl: navigationSlideshowTeamPrev.current,
              nextEl: navigationSlideshowTeamNext.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl =
                navigationSlideshowTeamPrev.current;
              swiper.params.navigation.nextEl =
                navigationSlideshowTeamNext.current;
            }}
            slidesPerView="auto"
            className="mb-16 team-slideshow carousel 1024:mb-24 group"
          >
            {props.content.team.map((member) => {
              return (
                <SwiperSlide
                  key={member.id}
                  className="team-member-slide hover:z-10"
                  onClick={() => {
                    openModal();
                    setModalData(member);
                  }}
                >
                  <TeamMemberCard data={member} />
                </SwiperSlide>
              );
            })}
            <div className="absolute z-10 w-full px-5 transition-opacity -translate-y-1/2 opacity-0 pointer-events-none navigation-wrapper top-1/2 768:px-8 duration-400 768:group-hover:opacity-100">
              <div
                ref={navigationSlideshowTeamPrev}
                className="absolute top-0 cursor-pointer pointer-events-auto slideshow-control prev-control left-4 768:left-8"
              >
                <svg
                  width="52"
                  height="52"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="26"
                    cy="26"
                    r="25"
                    fill="white"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M22.7319 36.9628L13.186 27.4168C12.6002 26.831 12.6002 25.8813 13.186 25.2955L22.7319 15.7496L24.8532 17.8709L17.868 24.8562L38.4658 24.8562L38.4658 27.8562L17.868 27.8562L24.8532 34.8414L22.7319 36.9628Z"
                    fill="black"
                  />
                </svg>
              </div>
              <div
                ref={navigationSlideshowTeamNext}
                className="absolute top-0 cursor-pointer pointer-events-auto slideshow-control next-control right-4 768:right-8"
              >
                <svg
                  width="52"
                  height="52"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="26"
                    cy="26"
                    r="25"
                    transform="rotate(-180 26 26)"
                    fill="white"
                    stroke="black"
                    strokeWidth="2"
                  />
                  <path
                    d="M29.2681 15.0372L38.814 24.5832C39.3998 25.169 39.3998 26.1187 38.814 26.7045L29.2681 36.2504L27.1468 34.1291L34.132 27.1438L13.5342 27.1438L13.5342 24.1438L34.132 24.1438L27.1468 17.1586L29.2681 15.0372Z"
                    fill="black"
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        ) : (
          <></>
        )}

        <Container>
          {props.content.clients ? (
            <div className="grid gap-4 mb-24 -mt-20 640:gap-12 768:gap-10 1024:gap-16 640:grid-cols-2">
              <h2 className="uppercase fluid-type">
                <AnimatedBox num={1}>
                  <span className="block 768:indent-56 1024:indent-68 1280:indent-96">
                    Our
                  </span>
                </AnimatedBox>
                <AnimatedBox num={2}>
                  <span className="block text-right 768:text-left optical-align letter-c">
                    clients
                  </span>
                </AnimatedBox>
              </h2>

              <div className="leading-tight break-all text-15 414:text-16 768:text-18 1280:text-22 columns-2 gap-x-0 414:gap-x-1 content-with-glyphs">
                {parse(props.content.clients)}
              </div>
            </div>
          ) : (
            <></>
          )}

          {props.content.awards ? (
            <div className="grid gap-4 mb-6 640:gap-12 768:gap-10 1024:gap-16 640:grid-cols-2">
              <h2 className="uppercase fluid-type">
                <AnimatedBox num={1}>
                  <span className="block optical-align letter-o">Our</span>
                </AnimatedBox>
                <AnimatedBox num={2}>
                  <span className="block text-right 768:text-left 768:indent-20 1024:indent-48 1280:indent-64">
                    awards
                  </span>
                </AnimatedBox>
              </h2>
              <div className="leading-tight text-15 414:text-16 768:text-18 1280:text-22 content-with-glyphs">
                <p>
                  {props.content.awards ? parse(props.content.awards) : <></>}
                </p>
              </div>
            </div>
          ) : (
            <></>
          )}
        </Container>
      </main>
    </>
  );
}

export async function getStaticProps(ctx) {
  const content = await API.getSingleType("about-page");

  return {
    props: {
      content: content,
    },
    revalidate: 10,
  };
}
